import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

export default function RefereeLoginPage() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login-referee", { username: form.username, password: form.password });
      setUser(data);
      toast.success(`Welcome, ${data.name || form.username}`);
      nav("/referee/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="referee-login-page" className="min-h-[calc(100vh-80px)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-jpa-ink">
        <img src="https://images.unsplash.com/photo-1544198365-f5d60b6d8190?w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-jpa-ink via-transparent to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <span className="bg-jpa-accent text-jpa-ink px-3 py-1 text-xs font-bold uppercase tracking-widest skew-badge w-fit">Referee portal</span>
          <h1 className="font-display text-7xl tracking-wider mt-4">CALL.<br />SCORE.<br />DECIDE.</h1>
          <p className="text-white/70 mt-4 text-sm max-w-md">Log in with the username &amp; password given to you by the tournament organizer.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-jpa-primary">
            <ShieldAlert className="w-4 h-4" /> Referee log in
          </div>
          <h2 className="font-display text-5xl tracking-wider mt-1">Court&nbsp;side</h2>
          <p className="text-jpa-muted mt-2 text-sm">Enter the username &amp; password provided by your tournament organizer.</p>

          <div className="mt-6">
            <Label>Username</Label>
            <Input
              required
              data-testid="ref-login-username"
              autoComplete="username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="mt-1 rounded-sm"
              placeholder="e.g. rahul01"
            />
          </div>
          <div className="mt-4">
            <Label>Password</Label>
            <PasswordInput
              required
              data-testid="ref-login-password"
              toggleTestId="ref-login-password-toggle"
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 rounded-sm"
            />
          </div>
          <Button disabled={loading} type="submit" data-testid="ref-login-submit" className="w-full mt-6 bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-sm h-12">
            {loading ? "Signing in..." : "Log in"}
          </Button>

          <div className="mt-6 text-xs text-jpa-muted text-center">
            If your username is used in multiple tournaments, contact your organizer for a unique username.
          </div>
        </form>
      </div>
    </div>
  );
}
